/**
 * Illuminated Ledger. Core tokens from 07-design-mockup-build-notes.md section 1
 * (column A); type hues from the critic's replacement map, column A; `spell`
 * from amendments 2.1 section 7. One change from those values, logged in
 * docs/reports/p0-9.md: `accent` in both modes, so it is clearly different
 * from `textMuted` (OKLab ΔE at least 12.0; it was 8.5 dark and 5.8 light).
 */
import { PALETTE } from '../palette.js';
export const ledger = {
    id: 'ledger',
    name: 'Illuminated Ledger',
    mood: 'Ink and vellum by lamplight: a well-kept GM binder, warm and scholarly. The manuscript feeling is an editorial flourish, not a costume.',
    modes: {
        dark: {
            core: {
                bg: '#1B1712',
                surface: '#241F19',
                surfaceRaised: '#2E2820',
                border: '#3A332A',
                borderStrong: '#4B443B',
                controlBorder: '#AA9C85',
                text: '#EDE4D3',
                textMuted: '#AA9C85',
                accent: '#E8A92A',
                accentContrast: '#1B1712',
                danger: '#E8867A',
                success: '#7FD0B8',
                ai: '#E8A92A',
            },
            types: {
                pc: '#7AC7FB',
                npc: '#4AF9E1',
                creature: '#FEBF88',
                encounter: '#FC998D',
                location: '#A3DDB2',
                faction: '#959DFE',
                item: '#B3C94D',
                lore: '#C498D1',
                arc: '#F47FA6',
                quest: '#F9D95E',
                script: '#DFAAC9',
                session: '#68E4F6',
                spell: '#93FEB8',
                note: '#AA9C85',
                deity: '#AA9C85',
            },
            palette: PALETTE.dark,
            elevation: { shadow: 'none', border: 'border' },
        },
        light: {
            core: {
                bg: '#F7F1E4',
                surface: '#FFFDF7',
                surfaceRaised: '#FFFFFF',
                border: '#DCCFB2',
                borderStrong: '#D2C5A9',
                controlBorder: '#6E624C',
                text: '#2B2418',
                textMuted: '#6E624C',
                accent: '#803300',
                accentContrast: '#FFFDF7',
                danger: '#A8281F',
                success: '#006850',
                ai: '#803300',
            },
            types: {
                pc: '#143464',
                npc: '#146869',
                creature: '#542613',
                encounter: '#9C4249',
                location: '#3B633D',
                faction: '#4757B5',
                item: '#564D15',
                lore: '#4C2483',
                arc: '#673445',
                quest: '#543B02',
                script: '#600744',
                session: '#0B546A',
                spell: '#00362E',
                note: '#6E624C',
                deity: '#6E624C',
            },
            palette: PALETTE.light,
            elevation: {
                shadow: '0 1px 2px rgb(43 36 24 / 0.08), 0 6px 16px rgb(43 36 24 / 0.1)',
                border: 'borderStrong',
            },
        },
    },
    // Deity keeps the neutral note treatment here: a pale silver-white hue does not fit
    // this direction's published color-distance floors (docs/reports/p0-9.md, Patch 1).
    userTypeHues: [],
    typography: {
        display: {
            family: '"EB Garamond", Garamond, "Adobe Garamond Pro", Georgia, serif',
            weight: 500,
            variation: 'normal',
        },
        heading: {
            family: '"EB Garamond", Garamond, "Adobe Garamond Pro", Georgia, serif',
            weight: 600,
            variation: 'normal',
        },
        body: {
            family: '"EB Garamond", Garamond, "Adobe Garamond Pro", Georgia, serif',
            weight: 400,
            variation: 'normal',
        },
        label: {
            family: '"Cinzel", "Trajan Pro", "Times New Roman", serif',
            weight: 600,
            variation: 'normal',
        },
        ui: {
            family: '"Inter", "Segoe UI", system-ui, sans-serif',
            weight: 400,
            variation: 'normal',
        },
        mono: {
            family: '"JetBrains Mono", "SFMono-Regular", Menlo, Consolas, monospace',
            weight: 400,
            variation: 'normal',
        },
        size: { title: 40, heading: 23, body: 18, ui: 14, label: 12 },
        bodyWeightDark: 440,
        labelCase: 'uppercase',
        labelTracking: '0.08em',
    },
    radii: { chip: 3, card: 4, control: 6 },
    ornament: {
        dropCapInRead: true,
        titleRule: true,
        scaleBar: false,
        divider: 'hairline',
        glow: false,
        statBlockEdge: 'accent-hairlines',
        secretBorder: 'textMuted',
        gmNotePanel: false,
    },
    fixes: [
        'Cinzel only on short labels (block labels and stat-block section heads, four words at most)',
        'Names and titles in EB Garamond',
        'The drop cap appears only in Read mode',
        'The type glyph beside the title replaces the wax seal',
        'Accent moved from #C9A24B / #7A5518 to #E8A92A / #803300, clearly apart from text-muted',
    ],
};
//# sourceMappingURL=ledger.js.map